import { useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, MoreHorizontal, FolderInput, Trash2, ExternalLink, File as FileIcon, Download } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { categoryLabel, isPdf, isVideo, openInNewTab } from "@/lib/utils";
import PdfThumbnail from "./PdfThumbnail";

function isGenericFile(image) {
  if (!image) return false;
  if (isPdf(image)) return false;
  if (isVideo(image)) return false;
  // Files routed to documents/cad are NEVER previewable as <img> even when
  // the browser tagged them image/vnd.dwg or similar.
  if (image.category === "documents" || image.category === "cad") return true;
  const mime = (image.mime || "").toLowerCase();
  if (mime.startsWith("image/")) return false;
  return true;
}

function extLabel(image) {
  const fn = (image?.filename || "").toLowerCase();
  if (fn.includes(".")) {
    const ext = fn.split(".").pop();
    if (ext && ext.length <= 5) return ext.toUpperCase();
  }
  const mime = (image?.mime || "").toLowerCase();
  if (mime) return mime.split("/").pop().slice(0, 5).toUpperCase();
  return "FILE";
}

function GenericFileTile({ image }) {
  const ext = extLabel(image);
  return (
    <div className="aspect-[4/5] w-full bg-gradient-to-br from-stone-200 to-stone-100 flex flex-col items-center justify-center px-4 py-6">
      <div className="w-16 h-20 rounded-md bg-white shadow-md border border-stone-200 flex items-center justify-center mb-3 relative">
        <FileIcon className="w-7 h-7 text-stone-400" strokeWidth={1.2} />
        <span className="absolute bottom-1 right-1 text-[8px] font-mono font-semibold text-stone-600 bg-stone-100 px-1 rounded">
          {ext}
        </span>
      </div>
      <p className="text-xs text-stone-700 font-medium text-center line-clamp-2 break-all">
        {image.filename || "Untitled"}
      </p>
    </div>
  );
}

function downloadFile(image) {
  if (!image?.data) return;
  const a = document.createElement("a");
  a.href = image.data;
  a.download = image.filename || "file";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/**
 * Image / PDF card with hover overlay.
 * Props:
 *   image
 *   onClick(image)               – primary click (open detail or new tab if PDF)
 *   onRequestDelete(image)       – opens cascade-aware delete dialog
 *   onMove(image, newCategory)   – move to another sub-category
 *   categories                   – array of category names available for "Move to"
 *   draggable                    – enable HTML5 drag for "drag to pill"
 *   testidPrefix
 *   index
 */
export default function ImageCard({
  image,
  onClick,
  onRequestDelete,
  onMove,
  onMoveRoom,
  categories = [],
  rooms = [],
  draggable = false,
  index = 0,
  testidPrefix = "image",
}) {
  const pdf = isPdf(image);
  const video = isVideo(image);
  const generic = isGenericFile(image);
  const [menuOpen, setMenuOpen] = useState(false);

  function handleClick() {
    if (pdf) {
      openInNewTab(image);
      return;
    }
    if (generic) {
      // Open in default application (new tab). Falls back to download.
      openInNewTab(image);
      return;
    }
    onClick?.(image);
  }

  function onDragStart(e) {
    if (!draggable) return;
    try {
      e.dataTransfer.setData("application/x-atelier-image-id", image.id);
      e.dataTransfer.setData("text/plain", image.id);
      e.dataTransfer.effectAllowed = "move";
    } catch (err) {
      // ignore
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: Math.min(index * 0.03, 0.4) }}
      className="break-inside-avoid mb-4 group relative cursor-zoom-in"
      onClick={handleClick}
      draggable={draggable}
      onDragStart={onDragStart}
      data-testid={`${testidPrefix}-card-${image.id}`}
      data-image-id={image.id}
    >
      <div className="overflow-hidden rounded-xl bg-stone-100">
        {pdf ? (
          <div className="aspect-[4/5]">
            <PdfThumbnail src={image.data} label={image.filename || "PDF"} />
          </div>
        ) : video ? (
          <video
            src={image.data}
            controls
            preload="metadata"
            className="w-full h-auto block bg-stone-900"
            onClick={(e) => e.stopPropagation()}
          />
        ) : generic ? (
          <GenericFileTile image={image} />
        ) : (
          <img
            src={image.data}
            alt={image.title || image.filename}
            loading="lazy"
            className="w-full h-auto block transition-transform duration-500 ease-out group-hover:scale-[1.04]"
          />
        )}
      </div>
      <div className="absolute inset-0 rounded-xl bg-gradient-to-t from-stone-900/60 via-stone-900/0 to-stone-900/0 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />

      {/* Top-left labels */}
      <div className="absolute top-2 left-2 flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
        <span className="bg-white/95 backdrop-blur text-stone-700 text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-full">
          {pdf ? "PDF" : video ? "VIDEO" : generic ? extLabel(image) : categoryLabel(image.category)}
        </span>
        {image.ai_analyzed && (
          <span className="bg-stone-900 text-white text-[10px] px-2 py-1 rounded-full flex items-center gap-1">
            <Sparkles className="w-2.5 h-2.5" />
            AI
          </span>
        )}
      </div>

      {/* Top-right actions */}
      <div
        className={`absolute top-2 right-2 flex items-center gap-1 transition-opacity ${
          menuOpen ? "opacity-100" : "opacity-0 group-hover:opacity-100"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {(onMove || onRequestDelete) && (
          <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                onClick={(e) => e.stopPropagation()}
                data-testid={`image-menu-${image.id}`}
                className="w-8 h-8 rounded-full bg-white/95 backdrop-blur flex items-center justify-center text-stone-700 hover:text-stone-900"
              >
                <MoreHorizontal className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              onClick={(e) => e.stopPropagation()}
              className="min-w-[180px]"
            >
              <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.15em] text-stone-500">
                {image.filename || "Image"}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {pdf && (
                <DropdownMenuItem
                  onClick={() => openInNewTab(image)}
                  data-testid={`image-menu-open-pdf-${image.id}`}
                >
                  <ExternalLink className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  Open PDF in new tab
                </DropdownMenuItem>
              )}
              {generic && (
                <DropdownMenuItem
                  onClick={() => downloadFile(image)}
                  data-testid={`image-menu-download-${image.id}`}
                >
                  <Download className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  Download file
                </DropdownMenuItem>
              )}
              {onMove && categories.length > 0 && (
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger data-testid={`image-menu-move-${image.id}`}>
                    <FolderInput className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    Move to
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent>
                    {categories.map((c) => (
                      <DropdownMenuItem
                        key={c}
                        disabled={c === image.category}
                        onClick={() => onMove(image, c)}
                        data-testid={`image-menu-move-${image.id}-${c}`}
                      >
                        {categoryLabel(c)}
                        {c === image.category && (
                          <span className="ml-auto text-[10px] text-stone-400">current</span>
                        )}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
              )}
              {onMoveRoom && rooms.length > 0 && (
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger data-testid={`image-menu-move-room-${image.id}`}>
                    <FolderInput className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    Link to room
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent>
                    <DropdownMenuItem
                      onClick={() => onMoveRoom(image, null)}
                      data-testid={`image-menu-room-${image.id}-none`}
                      disabled={!image.room_id}
                    >
                      No room
                      {!image.room_id && (
                        <span className="ml-auto text-[10px] text-stone-400">current</span>
                      )}
                    </DropdownMenuItem>
                    {rooms.map((r) => (
                      <DropdownMenuItem
                        key={r.id}
                        disabled={r.id === image.room_id}
                        onClick={() => onMoveRoom(image, r.id)}
                        data-testid={`image-menu-room-${image.id}-${r.id}`}
                      >
                        {r.name}
                        {r.id === image.room_id && (
                          <span className="ml-auto text-[10px] text-stone-400">current</span>
                        )}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
              )}
              {onRequestDelete && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => onRequestDelete(image)}
                    data-testid={`image-menu-delete-${image.id}`}
                    className="text-red-600 focus:text-red-700"
                  >
                    <Trash2 className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    Delete…
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        {onRequestDelete && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onRequestDelete(image);
            }}
            data-testid={`delete-image-${image.id}`}
            className="w-8 h-8 rounded-full bg-white/95 backdrop-blur flex items-center justify-center text-stone-700 hover:text-red-600"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" strokeWidth={1.5} />
          </button>
        )}
      </div>

      {image.title && !pdf && (
        <div className="absolute bottom-2 left-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
          <p className="text-xs text-white font-medium line-clamp-2">{image.title}</p>
        </div>
      )}
    </motion.div>
  );
}
